import React, { useState } from 'react';
import { Plus, MessageSquare, Calendar, DollarSign, Shield, FileText, Send, X } from 'lucide-react';

const initialThreads = [
  {
    id: "1",
    title: "New Chat",
    lastMessage: "Start a new conversation...",
    timestamp: new Date().toLocaleString('en-US', { weekday: 'long', hour: 'numeric', minute: '2-digit', hour12: true }),
    isActive: true
  }
];

// Updated templates: Only the first template is changed to "Contracts Expired"
const templates = [
  { icon: "bi-calendar", title: "Contracts Expired", sampleQuery: "List all expired contracts", description: "View all contracts that have expired.", isCompatible: true },
  { icon: "bi-currency-dollar", title: "Monthly Cashflow Summary", sampleQuery: "What's the total cashflow from active contracts this month?", description: "Monitor financial obligations.", isCompatible: true },
  { icon: "bi-shield-check", title: "Vendor Risk Assessment", sampleQuery: "Which vendors have high-risk contracts?", description: "Identify potential risks in vendor agreements.", isCompatible: true },
  { icon: "bi-file-earmark-text", title: "Key Clause Summary", sampleQuery: "Show key clauses for all active contracts.", description: "Review critical terms across contracts.", isCompatible: true },
];

function AskGenie() {
  const [query, setQuery] = useState('');
  const [selectedCitation, setSelectedCitation] = useState(null);
  const [threads, setThreads] = useState(initialThreads);
  const [activeThreadId, setActiveThreadId] = useState("1");
  const [threadMessages, setThreadMessages] = useState({ "1": [] });
  const [isLoading, setIsLoading] = useState(false);

  const getCurrentTime = () => {
    const now = new Date();
    return now.toLocaleString('en-US', { weekday: 'long', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const truncateTitle = (title, maxLength = 50) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength - 3) + '...';
  };

  const handleSelectThread = (threadId) => {
    setThreads(threads.map(t => ({ ...t, isActive: t.id === threadId })));
    setActiveThreadId(threadId);
  };

  const handleNewThread = () => {
    const newThread = {
      id: String(threads.length + 1),
      title: 'New Chat',
      lastMessage: 'Start a new conversation...',
      timestamp: getCurrentTime(),
      isActive: true
    };
    setThreads([...threads.map(t => ({ ...t, isActive: false })), newThread]);
    setActiveThreadId(newThread.id);
    setThreadMessages(prev => ({ ...prev, [newThread.id]: [] }));
  };

  const handleTemplateClick = (template, index) => {
    if (!template.isCompatible || !activeThreadId) return;

    const userMessage = { 
      id: `msg${Date.now()}`, 
      isUser: true, 
      content: template.sampleQuery, 
      timestamp: getCurrentTime() 
    };

    setIsLoading(true);
    setThreadMessages(prev => ({
      ...prev,
      [activeThreadId]: [...(prev[activeThreadId] || []), userMessage]
    }));

    setThreads(threads.map(t => 
      t.id === activeThreadId 
        ? { 
            ...t, 
            title: (threadMessages[activeThreadId]?.length === 0 ? truncateTitle(template.sampleQuery) : t.title),
            lastMessage: template.sampleQuery, 
            timestamp: getCurrentTime() 
          } 
        : t
    ));

    setTimeout(() => {
      let responseMessage;
      if (index === 0) { // Contracts Expired (Updated Template)
        responseMessage = { 
          id: `msg${Date.now() + 1}`, 
          isUser: false, 
          content: "Here are all expired contracts:", 
          timestamp: getCurrentTime(), 
          table: {
            columns: ["Contract", "Licensor", "Licensee", "Expired Date"],
            rows: [
              ["LiquidGenVendorAgreement", "LiquidGen, Inc.", "GBM Distributor Inc.", "December 31, 2024"],
              ["NewGenRetailAgreement", "NewGenRetail, Inc.", "Canada, Inc., GBM Limited", "April 30, 2023"],
              ["KAILA-MasterAgreement", "KAILA, LLC", "GBM Distributor Merchandising, Inc", "March 31, 2021"],
              ["KAILA-Amendmendt", "KAILA, LLC", "GBM Distributor, Inc.", "December 31, 2021"]
            ]
          },
          citations: ["LiquidGenVendorAgreement.pdf", "NewGenRetailAgreement.pdf", "KAILA-MasterAgreement.pdf", "KAILA-Amendmendt.pdf"]
        };
      } else if (index === 1) { // Monthly Cashflow Summary (Original)
        responseMessage = { 
          id: `msg${Date.now() + 1}`, 
          isUser: false, 
          content: "No active contracts found for this month.", 
          timestamp: getCurrentTime(), 
          citations: ["Contract Database Query: Status = 'Active'"]
        };
      } else if (index === 2) { // Vendor Risk Assessment (Original)
        responseMessage = { 
          id: `msg${Date.now() + 1}`, 
          isUser: false, 
          content: "All contracts are expired, so no high-risk vendors are currently active.", 
          timestamp: getCurrentTime(), 
          citations: ["Contract Risk Analysis Report"]
        };
      } else if (index === 3) { // Key Clause Summary (Original)
        responseMessage = { 
          id: `msg${Date.now() + 1}`, 
          isUser: false, 
          content: "No active contracts found to summarize key clauses.", 
          timestamp: getCurrentTime() 
        };
      } else {
        responseMessage = { 
          id: `msg${Date.now() + 1}`, 
          isUser: false, 
          content: "This template response is not yet implemented.", 
          timestamp: getCurrentTime() 
        };
      }

      setThreadMessages(prev => ({
        ...prev,
        [activeThreadId]: [...(prev[activeThreadId] || []), responseMessage]
      }));
      setIsLoading(false);
    }, 1500);
  };

  const handleQuerySubmit = () => {
    if (!query.trim() || !activeThreadId) return;

    const userMessage = { 
      id: `msg${Date.now()}`, 
      isUser: true, 
      content: query, 
      timestamp: getCurrentTime() 
    };

    setIsLoading(true);
    setThreadMessages(prev => ({
      ...prev,
      [activeThreadId]: [...(prev[activeThreadId] || []), userMessage]
    }));

    setThreads(threads.map(t => 
      t.id === activeThreadId 
        ? { 
            ...t, 
            title: (threadMessages[activeThreadId]?.length === 0 ? truncateTitle(query) : t.title),
            lastMessage: query, 
            timestamp: getCurrentTime() 
          } 
        : t
    ));

    setTimeout(() => {
      let responseMessage;
      const queryLower = query.toLowerCase();

      if (queryLower.includes("contracts expired") || queryLower.includes("list all expired contracts")) {
        responseMessage = { 
          id: `msg${Date.now() + 1}`, 
          isUser: false, 
          content: "Here are all expired contracts:", 
          timestamp: getCurrentTime(),
          table: {
            columns: ["Contract", "Licensor", "Licensee", "Expired Date"],
            rows: [
              ["LiquidGenVendorAgreement", "LiquidGen, Inc.", "GBM Distributor Inc.", "December 31, 2024"],
              ["NewGenRetailAgreement", "NewGenRetail, Inc.", "Canada, Inc., GBM Limited", "April 30, 2023"],
              ["KAILA-MasterAgreement", "KAILA, LLC", "GBM Distributor Merchandising, Inc", "March 31, 2021"],
              ["KAILA-Amendmendt", "KAILA, LLC", "GBM Distributor, Inc.", "December 31, 2021"]
            ]
          },
          citations: ["LiquidGenVendorAgreement.pdf", "NewGenRetailAgreement.pdf", "KAILA-MasterAgreement.pdf", "KAILA-Amendmendt.pdf"]
        };
      } else if (queryLower.includes("termination condition") || queryLower.includes("termination conditions")) {
        responseMessage = { 
          id: `msg${Date.now() + 1}`, 
          isUser: false, 
          content: "Termination conditions for specified contracts:", 
          timestamp: getCurrentTime(),
          table: {
            columns: ["Contract", "Expiry Date", "Conditions"],
            rows: [
              ["LiquidGenVendorAgreement", "December 31, 2024", "Initial Term: September 1, 2021 - December 31, 2024, Termination for cause: False reports, breach of warranty, failure to comply with specifications, etc."],
              ["NewGenRetailAgreement", "April 30, 2023", "Term: 4 years and 1 month from Effective Date, expiring April 30, 2023. Termination Rights: Failure to maintain insurance, bankruptcy, unauthorized assertion of rights, failure to make timely payments, unauthorized sales."],
              ["KAILA-MasterAgreement", "March 31, 2021", "Begins upon execution and ends on March 31, 2021, Conditions for termination outlined in Schedule 'A’"],
              ["KAILA-Amendmendt", "December 31, 2021", "Term ends on December 31, 2021 (amended from March 31, 2021)"]
            ]
          },
          citations: ["LiquidGenVendorAgreement.pdf", "NewGenRetailAgreement.pdf", "KAILA-MasterAgreement.pdf", "KAILA-Amendmendt.pdf"]
        };
      } else if (queryLower.includes("gbm limited") && queryLower.includes("status")) {
        responseMessage = { 
          id: `msg${Date.now() + 1}`, 
          isUser: false, 
          content: "Contracts with licensee GBM Limited and their statuses:", 
          timestamp: getCurrentTime(),
          table: {
            columns: ["Contract", "Effective Date", "Termination Date", "Status"],
            rows: [
              ["LiquidGenVendorAgreement", "September 1, 2021", "December 31, 2024", "Expired"],
              ["NewGenRetailAgreement", "April 1, 2019", "April 30, 2023", "Expired"],
              ["KAILA-MasterAgreement", "March 01, 2019", "March 31, 2021", "Expired"]
            ]
          },
          citations: ["LiquidGenVendorAgreement.pdf", "NewGenRetailAgreement.pdf", "KAILA-MasterAgreement.pdf"]
        };
      } else if (queryLower.includes("intellectual property") || queryLower.includes("ip rights")) {
        responseMessage = { 
          id: `msg${Date.now() + 1}`, 
          isUser: false, 
          content: "Intellectual Property Rights for specified contracts:", 
          timestamp: getCurrentTime(),
          list: [
            { item: "LiquidGenVendorAgreement:", details: [
              "All use of Licensed Marks by Licensee inures to Licensor's benefit.",
              "Licensor owns all rights, title, and interests in Licensed Marks.",
              "Works incorporating Licensed Marks are the exclusive property of Licensor."
            ]},
            { item: "NewGenRetailAgreement:", details: [
              "Sublicensor retains all rights, titles, and interests in the Film, the Property, and Protections (patent, copyright, trademark).",
              "Sublicensor has exclusive Broadcasting Rights, Video Rights, and Merchandising Rights in the Licensed Territory."
            ]},
            { item: "KAILA-MasterAgreement:", details: [
              "Ownership and usage rights of 'Coraline' licensed property.",
              "Licensor retains control over copyrighted material related to licensed products."
            ]}
          ],
          citations: ["[1] LiquidGenVendorAgreement.pdf", "[2] NewGenRetailAgreement.pdf", "[3] KAILA-MasterAgreement.pdf"]
        };
      } else if (queryLower.includes("payment terms") && queryLower.includes("liquidgen")) {
        responseMessage = { 
          id: `msg${Date.now() + 1}`, 
          isUser: false, 
          content: "Payment terms for the LiquidGen Contract (Bar Graph Data):", 
          timestamp: getCurrentTime(),
          barGraph: {
            labels: ["Year 1 Minimum", "Year 2 Minimum", "Year 3 Minimum"],
            data: [10000, 5000, 5000] // Based on Guaranteed Minimum Royalties
          },
          citations: ["LiquidGenVendorAgreement.pdf"]
        };
      } else {
        responseMessage = { 
          id: `msg${Date.now() + 1}`, 
          isUser: false, 
          content: "I'm not sure how to respond to that query yet.", 
          timestamp: getCurrentTime() 
        };
      }

      setThreadMessages(prev => ({
        ...prev,
        [activeThreadId]: [...(prev[activeThreadId] || []), responseMessage]
      }));
      setIsLoading(false);
      setQuery('');
    }, 1500);
  };

  const getBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case "expired":
        return "bg-danger";
      case "active":
        return "bg-success";
      case "pending":
        return "bg-info";
      default:
        return "bg-secondary";
    }
  };

  return (
    <div className="main-content p-0 d-flex" style={{ height: '100vh' }}>
      {/* Thread List */}
      <div className="col-3 border-end bg-light d-flex flex-column" style={{ maxWidth: '250px' }}>
        <div className="p-3 border-bottom">
          <button className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2" onClick={handleNewThread}>
            <Plus size={16} /> New Chat
          </button>
        </div>
        <div className="flex-grow-1 overflow-auto p-2">
          {threads.map(thread => (
            <button
              key={thread.id}
              className={`w-100 text-start p-2 mb-2 btn ${thread.isActive ? 'btn-light shadow-sm' : 'btn-light'}`}
              onClick={() => handleSelectThread(thread.id)}
            >
              <div className="d-flex align-items-start gap-2">
                <MessageSquare size={16} className="mt-1 text-muted" />
                <div className="flex-grow-1 overflow-hidden">
                  <div className="fw-medium small">{thread.title}</div>
                  <div className="text-muted text-truncate small">{thread.lastMessage}</div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>{thread.timestamp}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-grow-1 d-flex flex-column" style={{
        overflowY:'scroll',
        maxHeight: '100vh',
      }}>
        <header className="border-bottom bg-white p-3">
          <h1 className="h4 mb-0">Ask Genie</h1>
        </header>

        <div className="d-flex flex-grow-1">
          {/* Messages Area */}
          <div className="flex-grow-1 d-flex flex-column">
            <div className="flex-grow-1 p-4 overflow-auto">
              {(!activeThreadId || (threadMessages[activeThreadId] && threadMessages[activeThreadId].length === 0)) ? (
                <div className="text-center p-4">
                  <h2 className="h4 mb-2">Welcome to SpecAgent</h2>
                  <p className="text-muted mb-4">Get started with these popular queries or ask your own question.</p>
                  <div className="row row-cols-1 row-cols-md-2 g-3">
                    {templates.map((template, index) => (
                      <div className="col" key={index}>
                        <button
                          className={`card h-100 text-start p-3 ${template.isCompatible ? '' : 'opacity-50'} w-100`}
                          onClick={() => handleTemplateClick(template, index)}
                          disabled={!template.isCompatible}
                          style={{ maxWidth: '500px' }}
                        >
                          <div className="d-flex gap-3">
                            {template.icon === 'bi-calendar' && (
                              <Calendar 
                                size={24} 
                                className="p-2 bg-primary bg-opacity-10 rounded text-primary"
                                style={{ width: '40px', height: '40px' }}
                              />
                            )}
                            {template.icon === 'bi-currency-dollar' && (
                              <DollarSign 
                                size={24} 
                                className="p-2 bg-primary bg-opacity-10 rounded text-primary"
                                style={{ width: '40px', height: '40px' }}
                              />
                            )}
                            {template.icon === 'bi-shield-check' && (
                              <Shield 
                                size={24} 
                                className="p-2 bg-primary bg-opacity-10 rounded text-primary"
                                style={{ width: '40px', height: '40px' }}
                              />
                            )}
                            {template.icon === 'bi-file-earmark-text' && (
                              <FileText 
                                size={24} 
                                className="p-2 bg-primary bg-opacity-10 rounded text-primary"
                                style={{ width: '40px', height: '40px' }}
                              />
                            )}
                            <div>
                              <h5 className="fw-semibold">{template.title}</h5>
                              <p className="small text-muted">{template.description}</p>
                              <div className="small bg-light p-2 rounded">"{template.sampleQuery}"</div>
                            </div>
                          </div>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="container">
                  {(threadMessages[activeThreadId] || []).map(message => (
                    <div key={message.id} className={`d-flex ${message.isUser ? 'justify-content-end' : ''} mb-3`}>
                      <div className={`rounded p-3 ${message.isUser ? 'bg-primary text-white' : 'bg-white border'}`}>
                        <div>{message.content}</div>
                      {message.table && (
                          <div className="table-responsive mt-3">
                            <table className="table table-bordered" style={{ fontSize: '0.7rem', tableLayout: 'fixed' }}>
                              <thead className="table-light">
                                <tr>
                                  {message.table.columns.map((col, i) => (
                                    <th
                                      key={i}
                                      style={{
                                        padding: '12px 15px',
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',    
                                        overflowWrap: 'break-word',
                                        wordWrap: 'break-word',    
                                        maxWidth: '200px',         
                                        minWidth: '100px',       
                                        textTransform: 'capitalize',
                                      }}
                                    >
                                      {col}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {message.table.rows.map((row, i) => (
                                  <tr key={i}>
                                    {row.map((cell, j) => (
                                      <td
                                        key={j}
                                        style={{
                                          padding: '12px 15px',
                                        }}
                                      >
                                        {message.table.columns[j] === "Status" ? (
                                          <span className={`badge ${getBadgeClass(cell)}`}>{cell}</span>
                                        ) : (
                                          <h6 
                                          style={
                                            {
                                              maxWidth: '150px',
                                              fontSize: '0.7rem',
                                              fontWeight: 'normal',
                                              overflow: 'scroll',
                                              overflowWrap: 'break-word',
                                              wordWrap: 'break-word',
                                              // hide scrollbar
                                              scrollbarWidth: 'none',
                                              msOverflowStyle: 'none',
                                              '&::-webkit-scrollbar': {
                                                display: 'none',
                                              }
                                            }
                                          }
                                          >
                                            {cell}
                                          </h6>
                                        )}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {message.list && (
                          <ul className="list-group mt-3">
                            {message.list.map((itemObj, i) => (
                              <li key={i} className="list-group-item">
                                <strong>{itemObj.item}</strong>
                                <ul className="list-group list-group-flush mt-2">
                                  {itemObj.details.map((detail, j) => (
                                    <li key={j} className="list-group-item ps-3">{detail}</li>
                                  ))}
                                </ul>
                              </li>
                            ))}
                          </ul>
                        )}
                        {message.barGraph && (
                          <div className="mt-3">
                            <div className="bg-light p-3 rounded">
                              <div className="text-muted small mb-2">Payment Terms Visualization (Simplified Bar Graph - Guaranteed Minimum Royalties in USD)</div>
                              {message.barGraph.labels.map((label, i) => (
                                <div key={i} className="mb-2">
                                  <div className="d-flex justify-content-between">
                                    <span>{label}</span>
                                    <span>${message.barGraph.data[i].toLocaleString()}</span>
                                  </div>
                                  <div className="progress" style={{ height: '20px' }}>
                                    <div 
                                      className="progress-bar bg-primary" 
                                      style={{ width: `${(message.barGraph.data[i] / Math.max(...message.barGraph.data)) * 100}%` }}
                                    ></div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {message.citations && (
                          <div className="mt-3 pt-3 border-top">
                            <div className="small fw-medium mb-2">Citations:</div>
                            <div className="d-flex flex-wrap gap-2">
                              {message.citations.map((cit, i) => (
                                <button
                                  key={i}
                                  className="btn btn-sm btn-light"
                                  onClick={() => setSelectedCitation(typeof cit === 'string' ? { document: cit } : cit)}
                                >
                                  [{i + 1}] {typeof cit === 'string' ? cit : `${cit.document} (p.${cit.page})`}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className={`small ${message.isUser ? 'text-white-50' : 'text-muted'} mt-2`}>{message.timestamp}</div>
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="d-flex justify-content-start mb-3">
                      <div className="rounded p-3 bg-white border d-flex align-items-center">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="text-muted">Processing your request...</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-3 border-top bg-white">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ask me anything about your contracts..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleQuerySubmit()}
                />
                <button className="btn btn-primary" onClick={handleQuerySubmit}>
                  <Send size={16} />
                </button>
              </div>
            </div>
          </div>
           {/* Source Viewer */}
          <div className="flex-grow-1 col-3 border-start bg-light p-3" style={{ maxWidth: '250px' }}>
            {selectedCitation ? (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-semibold">Source Content</h6>
                  <button className="btn btn-sm btn-link" onClick={() => setSelectedCitation(null)}>
                    <X size={16} />
                  </button>
                </div>
                <div className="bg-white rounded p-3">
                  <div className="mb-2">
                    <div>{selectedCitation.document}</div>
                    {selectedCitation.page && selectedCitation.section && (
                      <div className="small text-muted">Page {selectedCitation.page}, {selectedCitation.section}</div>
                    )}
                  </div>
                  {selectedCitation.content && (
                    <div className="border-start border-primary border-3 ps-2">{selectedCitation.content}</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex align-items-center justify-content-center text-center text-muted p-3">
                <FileText size={24} className="mb-2" style={{ display: 'inline-block' }} />
                <p className="small">Click on a citation to view its content</p>
              </div>
            )}
          </div>
        </div>
       
      </div>
    </div>
  );
}

export default AskGenie;